import { Link } from 'react-router';
import { formatPeso, spendAmount, spendingIn } from '@tahi/core';
import type { TileSize } from '../../../tiles/registry';
import { Money } from '../../../ui';
import { fitFontSize } from '../../../ui/fitText';
import { EntryRow } from '../components/EntryRow';
import { copy } from '../copy';
import { categoryName, spokenPeso } from '../format';
import { useCategories, useEntries, useToday } from '../hooks';
import { FOCUS } from '../styles';

export function TodayTile({ size }: { size: TileSize; config: unknown }) {
  const today = useToday();
  const range = { start: today, end: today };
  const entries = useEntries(range);
  const rows = spendingIn(entries ?? [], range);
  const byId = new Map((useCategories() ?? []).map((c) => [c.id, c]));
  const total = rows.reduce((s, e) => s + spendAmount(e), 0);

  return (
    <div
      role="group"
      aria-busy={entries === undefined || undefined}
      className="flex flex-col gap-1"
      aria-label={`${copy.spentToday}, ${spokenPeso(total)}, ${copy.entriesCount(rows.length)}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/m/spending/entries"
          className={`inline-flex min-h-11 min-w-0 items-center text-sm text-muted ${FOCUS}`}
        >
          {rows.length === 0 ? copy.nothingYetToday : copy.entriesCount(rows.length)}
        </Link>
        <Link
          to="/m/spending/add"
          aria-label={copy.addSpending}
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-2xl text-fg ${FOCUS}`}
        >
          +
        </Link>
      </div>
      <div className="@container">
        <Money
          minor={total}
          className="block whitespace-nowrap leading-tight"
          style={{ fontSize: fitFontSize(formatPeso(total, { cents: 'always' }), 36) }}
        />
      </div>
      {size === 'wide' && rows.length > 0 ? (
        <ul className="mt-2 divide-y divide-divider">
          {rows.slice(0, 3).map((e) => (
            <EntryRow
              key={e.id}
              entry={e}
              categoryName={e.categoryId ? categoryName(e.categoryId, byId) : null}
              today={today}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
